import { Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ConsoleLogger } from '../../../core/utilities/console-logger';
import { ConversionService } from '../conversion.service';
import { ConversionClient } from '../models/ConversionClient';
import {
	ConversionClientDetail,
	ConversionClientNote,
	ConversionDebtor,
	ConversionDetails,
	ConversionInvoice,
	ConversionInvoiceNote,
} from '../models/ConversionDetails';
import {
	ConversionAtoBWalletFunding,
	ConversionDocument,
} from '../models/ConversionDataUpload';

@Component({
	selector: 'app-conversion-detail-modal',
	templateUrl: './conversion-detail-modal.component.html',
	styleUrls: ['./conversion-detail-modal.component.scss'],
	// The modal is rendered outside this component, so its width rule has to be global.
	encapsulation: ViewEncapsulation.None,
})
export class ConversionDetailModalComponent {
	@ViewChild('ConversionDetailModalTemplate')
	ConversionDetailModalTemplate: ElementRef;

	public currentRow: ConversionClient;
	public clientName = '';
	public clients: ConversionClientDetail[] = [];
	public missingDebtors: ConversionDebtor[] = [];
	public existingDebtors: ConversionDebtor[] = [];
	public notMovingDebtors: ConversionDebtor[] = [];
	public invoices: ConversionInvoice[] = [];
	public invoiceNotes: ConversionInvoiceNote[] = [];
	public clientNotes: ConversionClientNote[] = [];
	public documents: ConversionDocument[] = [];
	public atoBWallets: ConversionAtoBWalletFunding[] = [];
	public isLoading = false;
	public isLoadingDocuments = false;
	public isLoadingAtoB = false;
	public activeTab = 'client';

	private conversionDetailModal: NgbModalRef;

	constructor(
		private conversionService: ConversionService,
		private toastrService: ToastrService,
		private modalService: NgbModal
	) {}

	public openConversionDetailModal(row: ConversionClient) {
		this.currentRow = row;
		this.clientName = row ? row.clientName : '';
		this.clients = [];
		this.missingDebtors = [];
		this.existingDebtors = [];
		this.notMovingDebtors = [];
		this.invoices = [];
		this.invoiceNotes = [];
		this.clientNotes = [];
		this.documents = [];
		this.atoBWallets = [];
		this.activeTab = 'client';

		this.conversionDetailModal = this.modalService.open(
			this.ConversionDetailModalTemplate,
			{
				container: '.app',
				size: <any>'xl',
				backdrop: 'static',
				keyboard: false,
				windowClass: 'conversion-detail-modal',
			}
		);

		this.loadDetails();
	}

	public loadDetails() {
		if (!this.currentRow) {
			return;
		}

		this.isLoading = true;
		this.conversionService
			.getConversionDetails(this.currentRow.clientKeys)
			.toPromise()
			.then(
				(results: ConversionDetails) => {
					ConsoleLogger.logInfo(
						'conversionService.getConversionDetails',
						results
					);
					if (results) {
						this.clientName = results.clientName || this.clientName;
						this.clients = results.clients || [];
						this.missingDebtors = results.missingDebtors || [];
						this.existingDebtors = results.existingDebtors || [];
						this.notMovingDebtors = results.notMovingDebtors || [];
						// Only invoices still carrying a balance are worth reviewing here.
						this.invoices = (results.invoices || []).filter(
							(invoice) =>
								invoice.balance !== null &&
								invoice.balance !== undefined &&
								invoice.balance !== 0
						);
						// Already limited by the API to invoices that move (those with a balance).
						this.invoiceNotes = results.invoiceNotes || [];
						this.clientNotes = results.clientNotes || [];
					}
					this.isLoading = false;
					this.loadDocuments();
					this.loadAtoBWallets();
				},
				(error) => {
					console.error('Error loading conversion details:', error);
					this.toastrService.error(
						'Could not load the conversion details.',
						'Load Failed'
					);
					this.isLoading = false;
				}
			);
	}

	/** The carrier's WinFactor row: the one the documents (ClientID) and AtoB wallets are matched to. */
	private get winFactorClient(): ConversionClientDetail {
		const withId = this.clients.filter(
			(client) => client.winFactorId !== null && client.winFactorId !== undefined
		);
		const winFactor = withId.filter((client) => client.sourceSystem === 'WinFactor');
		return winFactor.length > 0 ? winFactor[0] : withId.length > 0 ? withId[0] : null;
	}

	/** WinFactor.ClientDocuments rows for the carrier's folder; step 7 sends the ones not yet in FactorCloud. */
	public loadDocuments() {
		const client = this.winFactorClient;
		if (!client) {
			return;
		}

		this.isLoadingDocuments = true;
		this.conversionService
			.getDocuments(client.winFactorId, false)
			.toPromise()
			.then(
				(results: ConversionDocument[]) => {
					this.documents = results || [];
					this.isLoadingDocuments = false;
				},
				(error) => {
					console.error('Error loading conversion documents:', error);
					this.toastrService.error(
						'Could not load the carrier documents.',
						'Load Failed'
					);
					this.isLoadingDocuments = false;
				}
			);
	}

	/** Active AtoB wallets matched to the carrier; step 1 adds each as an "AtoB Wallet" funding instruction. */
	public loadAtoBWallets() {
		const client = this.winFactorClient;
		const winFactorClientId = client ? client.winFactorId : null;
		const name = client ? client.clientName : this.clientName;
		if (winFactorClientId === null && !name) {
			return;
		}

		this.isLoadingAtoB = true;
		this.conversionService
			.getAtoBWalletsForClient(winFactorClientId, name)
			.toPromise()
			.then(
				(results: ConversionAtoBWalletFunding[]) => {
					this.atoBWallets = results || [];
					this.isLoadingAtoB = false;
				},
				(error) => {
					console.error('Error loading conversion AtoB wallets:', error);
					this.toastrService.error(
						'Could not load the carrier AtoB wallets.',
						'Load Failed'
					);
					this.isLoadingAtoB = false;
				}
			);
	}

	public get documentsToSendCount(): number {
		return this.documents.filter(
			(document) => !document.sentToFactorCloudDate && !document.factorCloudDocumentId
		).length;
	}

	public atoBWalletAction(wallet: ConversionAtoBWalletFunding): string {
		if (wallet.fundingInstructionSentDate) {
			return 'Already in FactorCloud';
		}
		return 'Will be added';
	}

	public formatFileSize(bytes: number): string {
		if (bytes === null || bytes === undefined) {
			return 'N/A';
		}
		if (bytes < 1024) {
			return bytes + ' B';
		}
		if (bytes < 1024 * 1024) {
			return (bytes / 1024).toFixed(1) + ' KB';
		}
		return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
	}

	public setActiveTab(tab: string) {
		this.activeTab = tab;
	}

	public isActiveTab(tab: string) {
		return this.activeTab === tab;
	}

	public closeConversionDetailModal() {
		if (this.conversionDetailModal) {
			this.conversionDetailModal.close();
		}
	}

	public isNull(value) {
		return value === null || value === undefined || value === '' ? 'N/A' : value;
	}

	// The API sends DateTime values with no offset ("2026-01-05T00:00:00"), which new Date()
	// reads as local midnight. Shifting that by the timezone offset moved every date back a
	// day for users west of UTC, so the calendar date is taken straight from the string.
	public formatDate(date: Date | string) {
		if (!date) {
			return '';
		}

		if (typeof date === 'string') {
			const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
			if (match) {
				return match[2] + '/' + match[3] + '/' + match[1];
			}
		}

		// Already a Date (or an unexpected string shape): format it as-is in local time.
		const datePipe = new DatePipe('en-US');
		return datePipe.transform(date, 'MM/dd/yyyy');
	}

	public get totalInvoiceAmount(): number {
		return this.invoices.reduce(
			(total, invoice) => total + (invoice.invoiceAmount || 0),
			0
		);
	}

	public get totalBalance(): number {
		return this.invoices.reduce(
			(total, invoice) => total + (invoice.balance || 0),
			0
		);
	}

	public get openInvoiceCount(): number {
		return this.invoices.filter((invoice) => invoice.isOpen === true).length;
	}
}



// WEBPACK FOOTER //
// ./src/app/portal/conversion/conversion-detail-modal/conversion-detail-modal.component.ts